import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ScanLine, Trash2 } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import {
  Champ,
  ChampPuces,
  ChampSelection,
  ChampTexte,
  MentionObligatoire,
} from "@/composants/ui/champ";
import { Chargement, Erreur } from "@/composants/ui/etats";
import {
  ChampsLot,
  lotVide,
  useCategoriesCommande,
  type ValeursLot,
} from "@/composants/formulaire-lot";
import {
  ADRESSE_VIDE,
  ChampCategorieCommande,
  ChampsAdresse,
  ChampsNatures,
  enAdresse,
  realisationRequise,
  type Natures,
  type ValeursAdresse,
} from "@/composants/champs-commande";
import { messageDe, useChargement } from "@/lib/chargement";
import {
  aujourdHui,
  CIVILITE_PAR_DEFAUT,
  OPTIONS_CIVILITE,
  TYPES_CLIENT,
} from "@/lib/libelles";
import {
  api,
  ErreurApi,
  type AffaireReconnue,
  type Client,
  type ClientReconnu,
  type Collegue,
} from "@/lib/api";
import { useSession } from "@/session";

/**
 * La création d'un dossier — la saisie manuelle du BDC signé.
 *
 *     « Les informations sont préremplies puis validées par un humain avant
 *       création définitive. » — V4.1 §4
 *
 * Cet écran EST l'écran de validation décrit par le §4. Le jour où LAB livrera
 * les documents anonymisés, l'extracteur remplira ces mêmes champs et rien
 * d'autre ne changera : c'est pour cela que la création est un seul appel.
 */

const OPTIONS_CLIENT = Object.entries(TYPES_CLIENT).map(([valeur, libelle]) => ({
  valeur,
  libelle,
}));

/**
 * Le client retrouvé par la découverte (ADR-0032). Sa `fiche` n'est là que s'il
 * est déjà dans le périmètre de l'utilisateur : le client d'un collègue se
 * reconnaît, il ne se lit pas — l'adresse de facturation se recopie alors du
 * BDC, comme pour un nouveau client.
 */
type ClientChoisi = { reconnu: ClientReconnu; fiche: Client | null };

const CLIENT_VIDE = {
  type: "PARTICULIER",
  civilite: CIVILITE_PAR_DEFAUT,
  nom: "",
  prenom: "",
  raison_sociale: "",
  email: "",
  telephone: "",
  adresse_ligne1: "",
  code_postal: "",
  ville: "",
};

export function PageCommandeNouvelle() {
  const naviguer = useNavigate();
  const session = useSession();
  const identite = session.statut === "connecte" ? session.identite : null;

  const collegues = useChargement<Collegue[]>(
    (signal) => api.proprietaires(signal),
    [],
  );

  const [dossier, setDossier] = useState({
    numero_commande_client: "",
    date_commande: aujourdHui(),
    date_signature: "",
    montant_ht: "",
    charge_affaires_id: "",
    tva_10_applicable: false,
    commentaires: "",
  });
  const [clientExistant, setClientExistant] = useState<ClientChoisi | null>(null);
  // L'affaire à laquelle rattacher la commande — ou "" pour en ouvrir une
  // nouvelle, qui est le défaut : « création implicite avec la première
  // commande afin de ne pas ajouter de saisie inutile » (LAB, 18/09 §1).
  const [affaireId, setAffaireId] = useState("");
  // Le nom a-t-il été saisi à la main ? Tant que non, il recopie la raison
  // sociale d'un professionnel.
  const [nomTouche, setNomTouche] = useState(false);
  const [client, setClient] = useState(CLIENT_VIDE);
  // Ce que la commande porte (ADR-0031). La catégorie part vide : aucune n'est
  // choisie d'office (LAB, 24/09). Livraison avec pose, le cas courant, l'est.
  const categories = useCategoriesCommande();
  const [categorie, setCategorie] = useState("");
  const [natures, setNatures] = useState<Natures>({ mode_remise: "LIVRAISON", avec_pose: true });
  const [realisation, setRealisation] = useState<ValeursAdresse>(ADRESSE_VIDE);
  // `null` tant qu'on n'y a pas touché : l'adresse de facturation suit alors
  // celle du client, sans effet ni recopie. Elle n'en est qu'un préremplissage :
  // un promoteur se fait facturer à son siège (ADR-0031).
  const [facturationSaisie, setFacturationSaisie] = useState<ValeursAdresse | null>(null);
  // Vide : la commande n'est pas découpée, et le serveur lui donne son lot
  // unique. L'écran ne parle de lot qu'à partir de deux.
  const [lots, setLots] = useState<ValeursLot[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  // Le chargé d'affaires par défaut, c'est soi-même quand on en est un : c'est
  // le cas le plus fréquent, et l'ADV corrigera d'un clic. En être un, c'est
  // figurer parmi ceux que le serveur propose.
  const moiChargeAffaires =
    identite && collegues.donnees?.some((collegue) => collegue.id === identite.id) ? identite.id : "";
  const chargeChoisi = dossier.charge_affaires_id || moiChargeAffaires;
  const professionnel = client.type === "PROFESSIONNEL";
  const fiche = clientExistant?.fiche ?? null;
  const adresseDuClient: ValeursAdresse = clientExistant
    ? {
        ligne1: fiche?.adresse_ligne1 ?? "",
        ligne2: fiche?.adresse_ligne2 ?? "",
        code_postal: fiche?.code_postal ?? "",
        ville: fiche?.ville ?? "",
      }
    : { ...ADRESSE_VIDE, ligne1: client.adresse_ligne1, code_postal: client.code_postal, ville: client.ville };
  const facturation = facturationSaisie ?? adresseDuClient;
  // LAB, 24/09 : aucune catégorie livrée. Tant que l'Administrateur n'en a
  // activé aucune, le serveur refuserait : l'écran le dit avant l'envoi.
  const sansCategorie = !categories.enCours && categories.options.length === 0;

  async function envoyer(evenement: React.FormEvent) {
    evenement.preventDefault();
    setErreur(null);
    setEnvoi(true);
    try {
      const fiche = await api.creerCommande({
        numero_commande_client: dossier.numero_commande_client,
        date_commande: dossier.date_commande,
        date_signature: dossier.date_signature || null,
        montant_ht: dossier.montant_ht || null,
        charge_affaires_id: chargeChoisi,
        tva_10_applicable: dossier.tva_10_applicable,
        commentaires: dossier.commentaires || null,
        categorie: categorie || null,
        ...natures,
        adresse_realisation: enAdresse(realisation),
        // `required` sur les champs : l'envoi n'a pas lieu sans elle.
        adresse_facturation: enAdresse(facturation) ?? { ...facturation, ligne2: null },
        client_id: clientExistant ? clientExistant.reconnu.id : null,
        affaire_id: clientExistant && affaireId ? affaireId : null,
        client_nouveau: clientExistant
          ? null
          : Object.fromEntries(
              Object.entries(client).map(([cle, valeur]) => [cle, valeur === "" ? null : valeur]),
            ),
        lots,
      });
      naviguer(`/commandes/${fiche.commande.id}`);
    } catch (cause) {
      setErreur(messageDe(cause));
      setEnvoi(false);
    }
  }

  return (
    <form className="flex flex-col gap-5" onSubmit={envoyer}>
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-encre-douce hover:text-encre"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Toutes les Commandes / Projets
      </Link>

      <div>
        <h1 className="text-xl font-semibold text-encre">Nouvelle Commande / Projet</h1>
        <p className="mt-1 text-sm text-encre-douce">
          Saisie d'après le bon de commande signé.
        </p>
        <MentionObligatoire className="mt-2" />
      </div>

      <p className="flex items-start gap-2 rounded-[6px] border border-filet bg-chene-clair/25 px-4 py-3 text-sm text-encre-douce">
        <ScanLine className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>
          La lecture automatique du BDC remplira ces champs. Elle attend les documents réels
          anonymisés que LAB doit fournir : on n'écrit pas un lecteur de documents sans documents.
          D'ici là, la saisie manuelle est le mode de fonctionnement normal.
        </span>
      </p>

      {erreur ? <Erreur message={erreur} /> : null}

      {sansCategorie ? (
        <Erreur message="Aucune catégorie commande n'est active : l'Administrateur doit en créer une dans Administration › Référentiels avant la première commande." />
      ) : null}

      <Carte>
        <EnTeteCarte>
          <h2 className="text-sm font-semibold text-encre">Le bon de commande</h2>
        </EnTeteCarte>
        <CorpsCarte>
          <div className="grid gap-4 sm:grid-cols-2">
            <Champ
              libelle="Numéro de commande"
              required
              maxLength={60}
              placeholder="CD000254"
              aide="Recopié du BDC. LABGEST n'en génère jamais (§3.1) et il ne se corrige plus ensuite."
              value={dossier.numero_commande_client}
              onChange={(e) =>
                setDossier({ ...dossier, numero_commande_client: e.target.value })
              }
            />
            {collegues.enCours ? (
              <Chargement message="Chargement des chargés d'affaires…" />
            ) : (
              <ChampSelection
                libelle="Chargé d'affaires"
                required
                options={[
                  { valeur: "", libelle: "— Choisir —" },
                  ...(collegues.donnees ?? []).map((collegue) => ({
                    valeur: collegue.id,
                    libelle: `${collegue.prenom} ${collegue.nom}`,
                  })),
                ]}
                value={chargeChoisi}
                onChange={(e) => setDossier({ ...dossier, charge_affaires_id: e.target.value })}
              />
            )}
            <Champ
              libelle="Date de commande"
              type="date"
              required
              value={dossier.date_commande}
              onChange={(e) => setDossier({ ...dossier, date_commande: e.target.value })}
            />
            <Champ
              libelle="Date de signature"
              type="date"
              value={dossier.date_signature}
              onChange={(e) => setDossier({ ...dossier, date_signature: e.target.value })}
            />
            <Champ
              libelle="Montant HT"
              type="number"
              min="0"
              step="0.01"
              value={dossier.montant_ht}
              onChange={(e) => setDossier({ ...dossier, montant_ht: e.target.value })}
            />
            <label className="flex items-center gap-2 self-end pb-3 text-sm text-encre">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={dossier.tva_10_applicable}
                onChange={(e) =>
                  setDossier({ ...dossier, tva_10_applicable: e.target.checked })
                }
              />
              TVA à 10 % (rénovation)
            </label>
            <ChampCategorieCommande
              categories={categories.options}
              valeur={categorie}
              onChanger={(choisie) => {
                setCategorie(choisie);
                // Un lot découpé avant ce choix n'a pas encore de catégorie : il
                // prend celle-ci. Celui qui en a une la garde.
                setLots((courants) =>
                  courants.map((lot) => (lot.categorie ? lot : { ...lot, categorie: choisie })),
                );
              }}
            />
            <ChampsNatures valeurs={natures} onChanger={setNatures} />
            <ChampTexte
              libelle="Commentaires"
              className="sm:col-span-2"
              value={dossier.commentaires}
              onChange={(e) => setDossier({ ...dossier, commentaires: e.target.value })}
            />
          </div>
        </CorpsCarte>
      </Carte>

      <Carte>
        <EnTeteCarte className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-encre">Le client</h2>
          {clientExistant ? (
            <Bouton
              type="button"
              taille="compact"
              variante="discret"
              onClick={() => {
                setClientExistant(null);
                setAffaireId("");
              }}
            >
              Saisir un nouveau client
            </Bouton>
          ) : null}
        </EnTeteCarte>
        <CorpsCarte>
          {clientExistant ? (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-encre">
                <span className="font-medium">{clientExistant.reconnu.designation}</span>
                {clientExistant.reconnu.ville ? ` — ${clientExistant.reconnu.ville}` : ""}
                <span className="block text-encre-douce">
                  {fiche
                    ? `${fiche.nombre_commandes} commande${fiche.nombre_commandes > 1 ? "s" : ""} à son nom que vous suivez.`
                    : "Client suivi par un collègue : vous n'en verrez que la commande que vous créez."}
                </span>
              </p>
              <ChoixAffaire
                clientId={clientExistant.reconnu.id}
                valeur={affaireId}
                onChanger={setAffaireId}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <ChoixClientExistant onChoisir={setClientExistant} />
              <div className="grid gap-4 border-t border-filet pt-4 sm:grid-cols-2">
                <ChampSelection
                  libelle="Type"
                  options={OPTIONS_CLIENT}
                  value={client.type}
                  onChange={(evenement) => {
                    const type = evenement.target.value;
                    // Repasser en particulier rend le nom à sa saisie propre :
                    // « Menuiseries Girondines » n'est pas le nom d'un ménage.
                    setNomTouche(false);
                    // La civilité suit le type, et ne se contente pas de
                    // disparaître : masquer le champ en laissant « M. » dans
                    // l'état enverrait une civilité à une entreprise, sans que
                    // personne puisse la voir ni la corriger.
                    setClient({
                      ...client,
                      type,
                      civilite: type === "PROFESSIONNEL" ? "" : CIVILITE_PAR_DEFAUT,
                      nom: type === "PROFESSIONNEL" ? client.raison_sociale : "",
                    });
                  }}
                />
                {client.type === "PROFESSIONNEL" ? null : (
                  <ChampPuces
                    libelle="Civilité"
                    options={OPTIONS_CIVILITE}
                    valeur={client.civilite}
                    onChanger={(civilite) => setClient({ ...client, civilite })}
                  />
                )}
                {professionnel ? (
                  <Champ
                    libelle="Raison sociale"
                    required
                    className="sm:col-span-2"
                    value={client.raison_sociale}
                    onChange={(evenement) => {
                      const raison_sociale = evenement.target.value;
                      // Le nom SUIT la raison sociale tant qu'on n'y a pas
                      // touché. Vider le champ « Nom » remet ce suivi en route :
                      // un champ vide veut dire « reprends la raison sociale »,
                      // et non « laisse-moi une fiche sans nom ».
                      setClient({
                        ...client,
                        raison_sociale,
                        nom: nomTouche ? client.nom : raison_sociale,
                      });
                    }}
                  />
                ) : null}
                <Champ
                  libelle="Nom"
                  required
                  value={client.nom}
                  onChange={(evenement) => {
                    const nom = evenement.target.value;
                    setNomTouche(nom !== "");
                    setClient({ ...client, nom });
                  }}
                />
                <Champ
                  libelle="Prénom"
                  value={client.prenom}
                  onChange={(e) => setClient({ ...client, prenom: e.target.value })}
                />
                <Champ
                  libelle="Adresse électronique"
                  type="email"
                  value={client.email}
                  onChange={(e) => setClient({ ...client, email: e.target.value })}
                />
                <Champ
                  libelle="Téléphone"
                  type="tel"
                  value={client.telephone}
                  onChange={(e) => setClient({ ...client, telephone: e.target.value })}
                />
                <Champ
                  libelle="Adresse"
                  className="sm:col-span-2"
                  value={client.adresse_ligne1}
                  onChange={(e) => setClient({ ...client, adresse_ligne1: e.target.value })}
                />
                <Champ
                  libelle="Code postal"
                  value={client.code_postal}
                  onChange={(e) => setClient({ ...client, code_postal: e.target.value })}
                />
                <Champ
                  libelle="Ville"
                  value={client.ville}
                  onChange={(e) => setClient({ ...client, ville: e.target.value })}
                />
              </div>
            </div>
          )}
        </CorpsCarte>
      </Carte>

      <Carte>
        <EnTeteCarte>
          <h2 className="text-sm font-semibold text-encre">Adresse de réalisation</h2>
        </EnTeteCarte>
        <CorpsCarte className="flex flex-col gap-3">
          <p className="text-sm text-encre-douce">
            {realisationRequise(natures)
              ? "Où la commande est livrée ou posée. Une commande, une adresse : deux adresses font deux commandes."
              : "Facultative pour une vente à l'emport sans pose."}
          </p>
          <ChampsAdresse
            prefixe="realisation"
            valeurs={realisation}
            onChanger={setRealisation}
            requise={realisationRequise(natures)}
          />
        </CorpsCarte>
      </Carte>

      <Carte>
        <EnTeteCarte>
          <h2 className="text-sm font-semibold text-encre">Adresse de facturation</h2>
        </EnTeteCarte>
        <CorpsCarte className="flex flex-col gap-3">
          <p className="text-sm text-encre-douce">
            Préremplie avec l'adresse du client ; elle peut en différer.
          </p>
          <ChampsAdresse
            prefixe="facturation"
            valeurs={facturation}
            onChanger={setFacturationSaisie}
            requise
          />
        </CorpsCarte>
      </Carte>

      {lots.map((lot, index) => (
        <Carte key={index}>
          <EnTeteCarte className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-encre">
              Lot {String(index + 1).padStart(2, "0")}
            </h2>
            {lots.length > 2 ? (
              <Bouton
                type="button"
                taille="compact"
                variante="discret"
                onClick={() => setLots(lots.filter((_, rang) => rang !== index))}
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                Retirer
              </Bouton>
            ) : null}
          </EnTeteCarte>
          <CorpsCarte>
            <ChampsLot
              prefixe={`lot-${index}-`}
              valeurs={lot}
              categories={categories.options}
              onChanger={(valeurs) =>
                setLots((courants) =>
                  courants.map((autre, rang) => (rang === index ? valeurs : autre)),
                )
              }
            />
          </CorpsCarte>
        </Carte>
      ))}

      <div className="flex flex-wrap items-center gap-2">
        {lots.length === 0 ? (
          <>
            <Bouton
              type="button"
              onClick={() => setLots([lotVide(categorie), lotVide(categorie)])}
            >
              Découper en lots
            </Bouton>
            <p className="text-sm text-encre-douce">
              Pour suivre séparément des réalisations : la cuisine réceptionnée, le dressing en
              attente.
            </p>
          </>
        ) : (
          <>
            <Bouton
              type="button"
              onClick={() => setLots((courants) => [...courants, lotVide(categorie)])}
            >
              Ajouter un lot
            </Bouton>
            <Bouton type="button" variante="discret" onClick={() => setLots([])}>
              Ne pas découper
            </Bouton>
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-2 border-t border-filet pt-5">
        <Bouton type="submit" variante="principal" disabled={envoi || sansCategorie}>
          {envoi ? "Création…" : "Créer la Commande / Projet"}
        </Bouton>
        <Bouton type="button" variante="discret" onClick={() => naviguer("/")} disabled={envoi}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

/**
 * Nouvelle affaire, ou rattachement à une affaire existante du client.
 *
 * Le client revient pour une salle de bains après sa cuisine : c'est le même
 * dossier, et LAB veut le lire d'un seul tenant (18/09 §1). Toutes les affaires
 * du client sont proposées, même celles d'un collègue : rattacher ne demande pas
 * de voir l'affaire, seulement qu'elle soit du même client (ADR-0032).
 */
function ChoixAffaire({
  clientId,
  valeur,
  onChanger,
}: {
  clientId: string;
  valeur: string;
  onChanger: (affaireId: string) => void;
}) {
  const affaires = useChargement<AffaireReconnue[]>(
    (signal) => api.affairesDuClient(clientId, signal),
    [clientId],
  );
  if (affaires.enCours) return <Chargement message="Recherche des affaires du client…" />;
  const existantes = affaires.donnees ?? [];
  if (existantes.length === 0) {
    return (
      <p className="text-sm text-encre-douce">
        Ce client n'a pas encore d'affaire : une nouvelle affaire sera ouverte avec cette commande.
      </p>
    );
  }
  return (
    <ChampSelection
      libelle="Affaire"
      aide="Une affaire regroupe les commandes d'un même client et d'un même chantier : cuisine, puis salle de bains. Le numéro de commande reste la référence Sage."
      options={[
        { valeur: "", libelle: "Nouvelle affaire (numérotée à la création)" },
        ...existantes.map((affaire) => ({ valeur: affaire.id, libelle: affaire.numero })),
      ]}
      value={valeur}
      onChange={(e) => onChanger(e.target.value)}
    />
  );
}

/**
 * Retrouver un client déjà connu, pour ne pas créer une seconde fiche.
 *
 * La recherche sort du périmètre (ADR-0032) : un chargé d'affaires retrouve le
 * client d'un collègue au lieu de le ressaisir en double. Elle ne rend que de
 * quoi le reconnaître ; sa fiche complète n'est lue que si on la voit déjà.
 */
function ChoixClientExistant({ onChoisir }: { onChoisir: (client: ClientChoisi) => void }) {
  const [terme, setTerme] = useState("");
  const [resultats, setResultats] = useState<ClientReconnu[] | null>(null);
  const [cherche, setCherche] = useState(false);

  async function chercher() {
    if (terme.trim().length < 2) return;
    setCherche(true);
    try {
      setResultats(await api.reconnaitreClients(terme.trim()));
    } finally {
      setCherche(false);
    }
  }

  async function choisir(reconnu: ClientReconnu) {
    try {
      onChoisir({ reconnu, fiche: (await api.client(reconnu.id)).client });
    } catch (cause) {
      // Hors de son périmètre, la fiche répond 404 : on la choisit quand même,
      // par son identité seule.
      if (cause instanceof ErreurApi && cause.statut === 404) onChoisir({ reconnu, fiche: null });
      else throw cause;
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-2">
        <Champ
          libelle="Ce client existe déjà ?"
          className="min-w-[14rem] flex-1"
          value={terme}
          placeholder="Nom, société, ville, téléphone…"
          onChange={(e) => setTerme(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              // Sans cela, la touche Entrée enverrait le formulaire entier
              // alors que l'utilisateur voulait seulement chercher.
              e.preventDefault();
              void chercher();
            }
          }}
        />
        <Bouton type="button" onClick={() => void chercher()} disabled={cherche}>
          {cherche ? "Recherche…" : "Chercher"}
        </Bouton>
      </div>
      {resultats !== null ? (
        resultats.length === 0 ? (
          <p className="text-sm text-encre-douce">
            Aucun client connu sous ce nom. Renseignez la fiche ci-dessous.
          </p>
        ) : (
          <ul className="divide-y divide-filet rounded-[6px] border border-filet">
            {resultats.map((client) => (
              <li key={client.id}>
                <button
                  type="button"
                  onClick={() => void choisir(client)}
                  className="flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-chene-clair/25"
                >
                  <span className="font-medium text-encre">{client.designation}</span>
                  <span className="text-encre-douce">
                    {[
                      [client.code_postal, client.ville].filter(Boolean).join(" "),
                      client.fin_telephone ? `tél. …${client.fin_telephone}` : null,
                    ]
                      .filter(Boolean)
                      .join(" — ")}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}
